import { useState } from 'react'
import { ErrorBanner, useAsync } from '../../components/Async'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { ApiError, credentialPeriod } from '../../lib/api'

const NAMES: Record<number, string> = {
  1: 'Every month',
  2: 'Every two months',
  3: 'Every three months',
  6: 'Every six months',
  12: 'Every year'
}

/**
 * How long a device credential lives before the fleet is handed a new one.
 *
 * It sits under Devices rather than under Settings because it is a property of the fleet, not of the
 * person looking at it. On the Check-in tab specifically, beside the cadence, because a credential
 * rotates only at a successful check-in: how often a device polls and how long its credential lasts
 * are two halves of one answer - how long a machine can be switched off and still come back.
 *
 * That leaves one cross-reference, and it is stated rather than assumed. New enrollment tokens last
 * exactly this long, and they are issued on the Enrollment tab, so an operator who set the period here
 * and then found their token expiring in thirty days would be reading two tabs to find one answer.
 * `credential-period.spec.ts` walks between the two rather than around them for that reason (D-429).
 *
 * The screen computes nothing. The rotation date and the fortnight of overlap are sent by the server,
 * so a console that has not been redeployed cannot tell an operator a different story from the one
 * the fleet is living.
 *
 * Choosing asks first. These are radio buttons, and a radio button is the one control somebody
 * presses to read a label rather than to make a decision — it used to apply on the click, to every
 * device in the organization, with no way back except pressing the one that was selected before and
 * hoping nothing had rotated in between. The question is a plain one rather than a typed phrase:
 * nothing is revoked early, so the cost of getting this wrong is a rotation schedule, not a fleet.
 */
export function CredentialPeriodCard() {
  const period = useAsync(() => credentialPeriod.get(), [])
  const [saving, setSaving] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [asking, setAsking] = useState<number | null>(null)

  async function choose(months: number) {
    setFailure(null)
    setSaving(true)
    try {
      await credentialPeriod.set(months)
      await period.reload()
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The credential period could not be changed.')
    } finally {
      setSaving(false)
    }
  }

  const current = period.value

  return (
    <div className="card">
      <div className="card-body">
        <h3 style={{ marginTop: 0 }}>Credential lifetime</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          How often every device in this organization is issued a new credential. New enrollment tokens
          last the same length of time.
        </p>

        <ErrorBanner message={failure ?? period.error} />

        {current && (
          <>
            <div className="theme-choices" role="radiogroup" aria-label="Credential lifetime">
              {current.offeredMonths.map((months) => (
                <label
                  key={months}
                  className={`theme-choice${current.periodMonths === months ? ' selected' : ''}`}
                  data-testid={`credential-period-${months}`}
                >
                  <input
                    type="radio"
                    name="credential-period"
                    value={months}
                    disabled={saving}
                    checked={current.periodMonths === months}
                    onChange={() => setAsking(months)}
                  />
                  <span className="theme-name">{NAMES[months] ?? `Every ${months} months`}</span>
                </label>
              ))}
            </div>

            <p className="hint" data-testid="credential-period-explanation">
              A device is handed its next credential after {current.rotateAfterDays} days and keeps the
              old one for a further {current.leadDays}, so it has {current.leadDays} days to pick the
              new one up. Nothing is revoked early: a device that has been switched off keeps working
              until the old credential actually expires.
              {!current.configured && ' Nobody has chosen a period yet, so this is the default.'}
            </p>
          </>
        )}
      </div>

      <ConfirmDialog
        open={asking !== null}
        title="Change the credential lifetime"
        confirmLabel={asking === null ? 'Change it' : `Change to ${(NAMES[asking] ?? `every ${asking} months`).toLowerCase()}`}
        busy={saving}
        onConfirm={() => {
          if (asking === null) return
          const months = asking
          setAsking(null)
          void choose(months)
        }}
        onClose={() => setAsking(null)}
        data-testid="credential-period-confirm"
      >
        <p>
          Every device in this organization moves onto the new schedule, and enrollment tokens issued
          from now on last {asking === null ? '' : asking === 1 ? 'a month' : `${asking} months`}. Kits
          already built keep the expiry they were built with.
        </p>
        <p className="hint">
          {current && asking !== null && asking < current.periodMonths
            ? 'This is shorter than the current period, so devices are handed a new credential sooner than they otherwise would have been. Nothing is revoked early and no device stops working.'
            : 'Nothing is revoked early and no device stops working. A device that has been switched off keeps working until its old credential actually expires.'}
        </p>
      </ConfirmDialog>
    </div>
  )
}
